<script setup>
import { tv } from 'tailwind-variants';
import { computed } from 'vue';

/**
 * @typedef {object} TextProps
 * @property {string} [className=''] - Additional class name for the text. Will be merged with the default class name.
 * @property {string} [tag=''] - HTML element tag to render
 * @property {string} [as=''] - HTML element tag to render
 * @property {('body-xs'|'body-sm'|'body-md'|'body-lg'|'heading-sm'|'heading-md'|'heading-lg'|'heading-xl'|'heading-2xl'|'heading-3xl')} [variant='body-sm'] - Text style variant
 * @property {('default'|'secondary'|'disabled'|'critical'|'success'|'caution'|'warning'|'magic'|'magic-secondary')} [variantColor='default'] - Text color variant
 * @property {('normal'|'medium'|'semibold'|'bold')} [weight='normal'] - Text weight variant
 * @property {boolean} [isLink=false] - Whether the text should be styled as a link
 */

/**
 * @type {TextProps}
 */
const props = defineProps({
  className: {
    type: String,
    default: '',
  },
  as: {
    type: String,
    default: '',
  },
  tag: {
    type: String,
    default: '',
  },
  variant: {
    type: String,
    default: 'body-md',
  },
  variantColor: {
    type: String,
    default: 'default',
  },
  weight: {
    type: String,
    default: 'normal',
  },
  isLink: {
    type: Boolean,
    default: false,
  },
});

const textRecipe = tv({
  base: '',
  variants: {
    variant: {
      'body-xs': 'text-[11px] leading-3 tracking-normal',
      'body-sm': 'text-xs leading-4 tracking-normal',
      'body-md': 'text-sm leading-5 tracking-normal',
      'body-lg': 'text-base leading-5 tracking-normal',
      'heading-sm': 'text-[13px] leading-5 tracking-normal',
      'heading-md': 'text-sm leading-4 tracking-normal',
      'heading-lg': 'text-xl leading-5 tracking-tight',
      'heading-xl': 'text-2xl leading-7 tracking-tight',
      'heading-2xl': 'text-3xl leading-[30px] tracking-tight',
      'heading-3xl': 'text-4xl leading-[36px] tracking-tighter',
    },
    variantColor: {
      default: 'text-brand-text',
      secondary: 'text-brand-text-secondary',
      disabled: 'text-brand-gray-11 cursor-not-allowed select-none',
      success: 'text-brand-green-14',
      caution: 'text-brand-yellow-14',
      warning: 'text-brand-orange-14',
      critical: 'text-brand-red-14',
      magic: 'text-brand-purple-14',
      'magic-secondary': 'text-brand-purple-13',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    isLink: {
      true: 'cursor-pointer text-brand-blue-11 hover:underline hover:text-brand-blue-12 active:text-brand-blue-13',
      false: '',
    },
    /**
     * Special case. Chatwoot have built in global style for 'p', 'h1', 'h2' tag. And we need to normalize it.
     * @see application.scss
     */
    reset: {
      true: 'mb-0',
    },
  },
  defaultVariants: {
    variant: 'body-sm',
    variantColor: 'default',
    weight: 'normal',
    reset: true,
  },
});

const getTextTag = computed(() => {
  if (props.tag || props.as) return props.tag || props.as;

  const bodyVariants = ['body-xs', 'body-sm', 'body-md', 'body-lg'];
  if (bodyVariants.includes(props.variant)) {
    return 'p';
  }

  const headingMap = {
    'heading-3xl': 'h1',
    'heading-2xl': 'h2',
    'heading-xl': 'h3',
    'heading-lg': 'h4',
    'heading-md': 'h5',
    'heading-sm': 'h6',
  };

  return headingMap[props.variant] || 'p';
});
</script>

<template>
  <component
    :is="getTextTag"
    data-component="CsText"
    :data-variant="props.variant"
    :data-color="props.color"
    :class="textRecipe(props, { className: props.className })"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <slot />
  </component>
</template>
